import Image from 'next/image';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import SettingsIcon from '@mui/icons-material/Settings';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import logoLumniBranca from '@/public/img/logos/logo_lumni_branca.svg';

import React, { ComponentType, FC } from 'react';
import {
  CustomSidebarButton,
  CustomSidebarGroup,
  SectionTitle,
} from './CustomSidebar';
import QueryStatsOutlined from '@mui/icons-material/QueryStatsOutlined';
import { routes } from '@/config/routes';
import { AuthGate } from '@/config/auth';
import {
  PERMISSION_USERS_ADD_BASEUSER,
  PERMISSION_USERS_VIEW_ANALISAR,
  PERMISSION_USERS_VIEW_VISAO_MERCADO,
  PERMISSION_USERS_VIEW_COMPARAR_ESCOLAS,
  PERMISSION_PNAE_VIEW_REAJUSTEPNAE,
} from '@/config/permissions';
import InboxOutlined from '@mui/icons-material/InboxOutlined';
import WorkspacePremiumOutlined from '@mui/icons-material/WorkspacePremiumOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';

interface CustomDrawerContentsProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  CustomDrawerHeader: ComponentType<{
    sx?: object;
    children?: React.ReactNode;
  }>;
}

export const CustomDrawerContents: FC<CustomDrawerContentsProps> = ({
  isOpen,
  toggleSidebar,
  CustomDrawerHeader,
}) => (
  <Stack p='24px 16px' gap={3}>
    <CustomDrawerHeader sx={{ padding: '0 8px !important' }}>
      <IconButton
        disableRipple
        onClick={toggleSidebar}
        color='inherit'
        sx={{ py: 0 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
          gap={2}
        >
          {isOpen && (
            <Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
              <MenuOpenIcon sx={{ marginRight: '14px' }} />
              <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                <Image
                  src={logoLumniBranca}
                  alt='Logo Lumni Branca'
                  height={24}
                />
              </Stack>
            </Stack>
          )}
          {!isOpen && <MenuOpenIcon sx={{ transform: 'rotate(180deg)' }} />}
        </Box>
      </IconButton>
    </CustomDrawerHeader>
    <Stack>
      <CustomSidebarButton
        name='Início'
        Icon={HomeOutlined}
        path={routes.home}
      />
      {/* AuthGate da Seção Pedagógico */}
      <SectionTitle title='Pedagógico' />
      <CustomSidebarButton
        name='Escolas no ENEM'
        Icon={SchoolOutlined}
        path={routes.escolas_enem}
      />
      {/* AuthGate da Seção de Negócios */}
      <AuthGate
        permissions={[
          PERMISSION_USERS_VIEW_ANALISAR,
          PERMISSION_USERS_VIEW_VISAO_MERCADO,
          PERMISSION_USERS_VIEW_COMPARAR_ESCOLAS,
          PERMISSION_PNAE_VIEW_REAJUSTEPNAE,
        ]}
      >
        <SectionTitle title='Negócios' />
        {/* AuthGate do Botão de Visão do mercado */}
        <AuthGate permissions={[PERMISSION_USERS_VIEW_VISAO_MERCADO]}>
          <CustomSidebarButton
            name='Visão do Mercado'
            Icon={InboxOutlined}
            path={routes.visao_mercado}
          />
        </AuthGate>
        {/* AuthGate do Botão de Central do Reajuste */}
        <AuthGate permissions={[PERMISSION_PNAE_VIEW_REAJUSTEPNAE]}>
          <CustomSidebarButton
            name='Central do Reajuste'
            Icon={PaymentsOutlined}
            path={routes.central_reajuste}
          />
        </AuthGate>
        {/* AuthGate do Botão de Analisar */}
        <AuthGate permissions={[PERMISSION_USERS_VIEW_ANALISAR]}>
          <CustomSidebarButton
            name='Analisar Escolas'
            Icon={QueryStatsOutlined}
            path={routes.analisar}
          />
        </AuthGate>
        <AuthGate permissions={[PERMISSION_USERS_VIEW_COMPARAR_ESCOLAS]}>
          <CustomSidebarButton
            name='Comparar Escolas'
            Icon={WorkspacePremiumOutlined}
            path={routes.comparar_escolas}
          />
        </AuthGate>
      </AuthGate>
      {/* AuthGate da Seção de Configurações */}
      <AuthGate permissions={[PERMISSION_USERS_ADD_BASEUSER]}>
        <SectionTitle title='Configurações' />
        {/* AuthGate do Grupo de Configurações */}
        <AuthGate permissions={[PERMISSION_USERS_ADD_BASEUSER]}>
          <CustomSidebarGroup name='Configurações' Icon={SettingsIcon}>
            <AuthGate permissions={[PERMISSION_USERS_ADD_BASEUSER]}>
              {/* AuthGate do Botão de Usuários */}
              <CustomSidebarButton
                name='Usuários'
                Icon={AccountCircleIcon}
                path={routes.usuarios}
                isIndented={isOpen}
              />
            </AuthGate>
          </CustomSidebarGroup>
        </AuthGate>
      </AuthGate>
    </Stack>
  </Stack>
);
